import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React, {useEffect, useRef, useState} from 'react';
import isEqual from 'react-fast-compare';
import {CSSTransition, TransitionGroup} from 'react-transition-group';

import Banner from 'buffers/Banner';
import * as DatetimeFields from 'notebook/data-tables/DatetimeFields';
import DocumentEditorContext from 'notebook/documents/DocumentEditorContext';
import FormattersConstants from 'notebook/mediocre/formatted-text/FormattersConstants';
// @ts-expect-error
import * as selutil from 'notebook/mediocre/selection/text/SelUtil';
import {Cell, FormulaAlignment} from 'notebook/tables/constants/CellConstants';
import {RowMetadata} from 'notebook/tables/constants/RowConstants';
import {Table, TableFunction, TableSetting} from 'notebook/tables/constants/TableConstants';
import {MultiSelectionBox, TableIndex} from 'notebook/tables/constants/TableSelectionConstants';
import FormulaEditable from 'notebook/tables/core-ui/FormulaEditable';
import FunctionDropdown from 'notebook/tables/core-ui/FunctionDropdown';
import {TriangleState} from 'notebook/tables/errors/TableErrorConstants';
import excess from 'notebook/tables/excess';
import {EvaluatedCellError} from 'notebook/tables/excess/EvaluationConstants';
import {functions} from 'notebook/tables/excess/functions';
import SpreadsheetStore from 'notebook/tables/spreadsheets/SpreadsheetStore';
import {FormatToggleButton} from 'notebook/tables/toolbar/FormatToggleButton';
import {LinkModalButton} from 'notebook/tables/toolbar/LinkModalButton';
import SetPrecisionModalClass from 'notebook/tables/toolbar/SetPrecisionModal';
import {TextAlignmentDropdown} from 'notebook/tables/toolbar/TextAlignmentDropdown';
import getTestProps from 'test/lib/cypress/getTestProps';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import * as ColorPickerConstants from 'ui-library/ColorPickerConstants';
import ColorPickerDropdown from 'ui-library/dropdowns/ColorPickerDropdown';
import Dropdown from 'ui-library/dropdowns/Dropdown';
import {FloatingPlacement} from 'ui-library/floating-ui/FloatingConstants';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import useStoreSelector from 'ui-library/hooks/useStoreSelector';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import Menu from 'ui-library/menus/Menu';
import Option from 'ui-library/menus/Option';
import Section from 'ui-library/menus/Section';
import * as TransitionConstants from 'ui-library/TransitionConstants';
import {Optional} from 'util/Types';

import ErrorTrianglePopover from '../errors/ErrorTrianglePopover';

export interface FormatPropertyChange {
  property: string;
  value?: unknown;
}

export interface AlignmentState {
  active?: FormulaAlignment;
  default: FormulaAlignment;
}

export interface FormulaBoxProps {
  /**
   * Additional buttons to render in formula bar, between the format and setting buttons
   */
  additionalButtons?: JSX.Element | null;
  additionalFunctions?: Array<TableFunction>;
  additionalSettings?: Array<TableSetting>;
  banner?: string | JSX.Element;
  createSelectedCellsUpdate: (
    applySettingToSelection: (
      args: MultiSelectionBox & {
        table: Table;
        onUpdateCells: (newCells: Array<Array<Cell>>) => void;
      }
    ) => void
  ) => void;
  cellEnterHandler: (e: {
    text: string;
    cellData: Partial<Cell>;
    enterKey?: boolean;
    shiftKey?: boolean;
  }) => void;
  cellEscapeHandler: () => void;
  cid: string;
  disableFormulas?: boolean;
  error: Optional<EvaluatedCellError>;
  fileId: string;
  formatting: {
    bold?: boolean;
    italic?: boolean;
    underline?: boolean;
    strikethrough?: boolean;
    align: AlignmentState;
    link: {
      url?: Optional<string>;
      text?: string;
    };
    foregroundColor?: string;
    backgroundColor?: string;
    important?: boolean;
    scientificNotation?: boolean;
    precision?: number;
    merge: {
      active?: boolean;
      disabled?: boolean | undefined;
    };
    datetime: boolean;
  };
  getLastIndex: () => TableIndex;
  disabled: boolean;
  disabledCellLinking: boolean;
  disabledCellEditing: boolean;
  mergeHandler: ((merge: boolean) => void) | undefined;
  mouseDownHandler: () => void;
  multiSelIndex: TableIndex | null;
  /**
   * Number of columns in the selection that support precision and scientific notation.
   */
  numColumnsSupportingPrecision: number;
  onFormulaFocus: (text: string) => void;
  onFunctionClick: (tableFunction: TableFunction) => void;
  onCellPropertyChange: (updates: Array<FormatPropertyChange>) => void;
  openSetSelectorModal: () => void;
  rowMetadata: RowMetadata | null;
  selectorsEnabled: boolean;
  tableName: string;
  tableNamespace: string;
}

const getNormalizedColor = (color: string): string | null => {
  if (color === ColorPickerConstants.COLORS.NO_STYLE) {
    return null;
  }
  return color;
};

export default function FormulaBox({
  additionalButtons = null,
  additionalFunctions = [],
  additionalSettings = [],
  banner,
  cellEnterHandler,
  cellEscapeHandler,
  cid,
  createSelectedCellsUpdate,
  disableFormulas = false,
  error,
  fileId,
  formatting,
  getLastIndex,
  disabled,
  disabledCellLinking,
  disabledCellEditing,
  mergeHandler,
  mouseDownHandler,
  multiSelIndex,
  numColumnsSupportingPrecision,
  onFormulaFocus,
  onCellPropertyChange,
  onFunctionClick,
  openSetSelectorModal,
  rowMetadata,
  selectorsEnabled,
  tableName,
  tableNamespace,
}: FormulaBoxProps): JSX.Element {
  const {documentName} = DocumentEditorContext.useContext();
  const [isFunctionDropdownOpen, setIsFunctionDropdownOpen] = useState(false);
  const [showSetPrecisionModal, setShowSetPrecisionModal] = useState(false);
  const formulaEditableRef = useRef<HTMLDivElement>(null);

  const formulaBarSelection = useStoreSelector(
    SpreadsheetStore,
    (store) => {
      const storeState = store.getState(tableNamespace, tableName, fileId);
      const selection = storeState.selection;
      // Only update selection if it's for the formula bar - avoid re-renders on other selection changes
      if (selection?.cid === `${cid}-formula-box`) {
        return selection;
      }
      return null;
    },
    isEqual
  );

  useEffect(() => {
    if (formulaBarSelection && formulaEditableRef.current) {
      const formulaEditableElement = formulaEditableRef.current;
      if (formulaEditableElement) {
        selutil.restoreSelection(formulaEditableElement, {
          start: formulaBarSelection.start,
          end: formulaBarSelection.end,
        });
      }
    }
  }, [formulaBarSelection]);

  const disabledMerging =
    disabled || disabledCellEditing || formatting.merge.disabled || mergeHandler == null;
  const disabledFormulaBar = disabledCellEditing || multiSelIndex == null;
  const disabledNumericFormatting = disabled || numColumnsSupportingPrecision === 0;
  const disabledNumericFormattingTooltip = disabledNumericFormatting
    ? 'No supported numeric columns selected'
    : null;

  const setSelectionLink = (link: {text?: string; url?: string}): void => {
    const updates: Array<FormatPropertyChange> = [];
    if (link.url !== formatting.link.url) {
      updates.push({property: 'link', value: link.url});
    }
    if (link.text !== formatting.link.text) {
      updates.push({property: 'text', value: link.text});
    }
    onCellPropertyChange(updates);
  };

  const formattedError = error && (
    <ErrorTrianglePopover
      key="error-popover"
      errorState={TriangleState.ILLEGAL}
      error={error}
      placement={FloatingPlacement.BOTTOM}
    >
      <span className="u-valign-middle u-tiny-spacing-left text-danger">
        <BenchlingIcon icon="warning" />
      </span>
    </ErrorTrianglePopover>
  );

  const toolbar = (
    <>
      <div className="buttonGroup u-tiny-padding-right formulaBox-formatButtonGroup">
        <FormatToggleButton
          format={FormattersConstants.BOLD}
          tooltip="Bold"
          icon="format-bold"
          active={Boolean(formatting.bold)}
          disabled={disabled}
          onClick={(property, value) => {
            onCellPropertyChange([{property, value}]);
          }}
        />
        <FormatToggleButton
          format={FormattersConstants.ITALIC}
          tooltip="Italic"
          icon="format-italic"
          active={Boolean(formatting.italic)}
          disabled={disabled}
          onClick={(property, value) => {
            onCellPropertyChange([{property, value}]);
          }}
        />
        <FormatToggleButton
          format={FormattersConstants.UNDERLINE}
          tooltip="Underline"
          icon="format-underline"
          active={Boolean(formatting.underline)}
          disabled={disabled}
          onClick={(property, value) => {
            onCellPropertyChange([{property, value}]);
          }}
        />
        <FormatToggleButton
          format={FormattersConstants.STRIKETHROUGH}
          tooltip="Strikethrough"
          icon="format-strikethrough"
          active={Boolean(formatting.strikethrough)}
          disabled={disabled}
          onClick={(property, value) => {
            onCellPropertyChange([{property, value}]);
          }}
        />
        <LinkModalButton
          link={formatting.link}
          disabled={disabled || disabledCellLinking || disabledCellEditing}
          onChange={setSelectionLink}
        />
      </div>
      <div className="buttonGroup u-tiny-padding-right">
        <ColorPickerDropdown
          iconAltText="Color cell"
          backgroundColorLink={{
            value: formatting.backgroundColor,
            requestChange: (color) => {
              onCellPropertyChange([{property: 'backgroundColor', value: getNormalizedColor(color)}]);
            },
          }}
          foregroundColorLink={{
            value: formatting.foregroundColor,
            requestChange: (color) => {
              onCellPropertyChange([{property: 'foregroundColor', value: getNormalizedColor(color)}]);
            },
          }}
          className={classNames({
            'is-active': Boolean(formatting.foregroundColor || formatting.backgroundColor),
            'is-disabled': disabled || disabledCellEditing,
          })}
          disabled={disabled || disabledCellEditing}
        />
      </div>
      <div className="u-tiny-padding-right">
        <TextAlignmentDropdown
          selected={formatting.align.active}
          iconFormat={formatting.align.default}
          disabled={disabled}
          onSelect={(alignment) => {
            onCellPropertyChange([{property: 'align', value: alignment}]);
          }}
        />
      </div>
      {mergeHandler != null && (
        <div className="buttonGroup u-tiny-padding-right">
          <Tooltip text="Merge Cells">
            <Button
              aria-pressed={Boolean(formatting.merge.active)}
              buttonStyle={ButtonStyle.TERTIARY_GRAY}
              className={classNames({
                'is-active': formatting.merge.active,
                'is-disabled': disabledMerging,
              })}
              data-fmt="merge"
              icon="table-merge"
              iconAltText="Merge cells"
              key="merge"
              onClick={() => {
                mergeHandler(!formatting.merge.active);
              }}
              size={ButtonSize.MINI}
              disabled={disabledMerging}
            />
          </Tooltip>
        </div>
      )}
      {formatting.datetime && (
        <div className="buttonGroup u-tiny-padding-right">
          <Tooltip text="Insert current time">
            <Button
              buttonStyle={ButtonStyle.TERTIARY_GRAY}
              icon="eln-timestamp"
              iconAltText="Insert current time"
              onClick={() => {
                onCellPropertyChange([{property: 'text', value: DatetimeFields.now()}]);
              }}
              size={ButtonSize.MINI}
              disabled={disabled || disabledCellEditing}
            />
          </Tooltip>
        </div>
      )}
      {additionalButtons && <div className="buttonGroup u-tiny-padding-right">{additionalButtons}</div>}
      <div className="buttonGroup u-tiny-padding-right">
        <Dropdown
          menu={
            <Menu height={Menu.HEIGHTS.SMALL} width={Menu.WIDTHS.FIT_CONTENT} aria-label="Settings menu">
              <Section>
                {selectorsEnabled && (
                  <Option key="selector" onSelect={openSetSelectorModal}>
                    Selector
                  </Option>
                )}
                <Option
                  key="important"
                  onSelect={() => {
                    onCellPropertyChange([{property: 'important', value: !formatting.important}]);
                  }}
                  isDisabled={disabledCellEditing}
                >
                  Toggle important
                </Option>
                <Option
                  key="scientific"
                  onSelect={() => {
                    onCellPropertyChange([
                      {property: 'scientificNotation', value: !formatting.scientificNotation},
                    ]);
                  }}
                  isDisabled={disabledNumericFormatting}
                  tooltipTitle={disabledNumericFormattingTooltip}
                  tooltipPlacement={FloatingPlacement.RIGHT}
                >
                  Toggle scientific notation
                </Option>
              </Section>
              <Section>
                <Option
                  key="precision"
                  onSelect={() => {
                    setShowSetPrecisionModal(true);
                  }}
                  isDisabled={disabledNumericFormatting}
                  tooltipTitle={disabledNumericFormattingTooltip}
                  tooltipPlacement={FloatingPlacement.RIGHT}
                >
                  Set precision
                </Option>
                {additionalSettings.map((item) => (
                  <Option
                    key={`setting-${item.name}`}
                    onSelect={() => {
                      createSelectedCellsUpdate(item.applySettingToSelection);
                    }}
                  >
                    {item.name}
                  </Option>
                ))}
              </Section>
            </Menu>
          }
        >
          <Button
            buttonStyle={ButtonStyle.TERTIARY_GRAY}
            disabled={disabled}
            icon="settings"
            iconAltText="Settings"
            isDropdown={true}
            size={ButtonSize.MINI}
          />
        </Dropdown>
      </div>
    </>
  );

  const box = (
    <div
      className="table-floater u-small-spacing-right"
      key={`table-floater-${cid}`}
      {...getTestProps({component: 'FormulaBox', element: 'root'})}
    >
      <div className={classNames('u-flex-display', css(styles.formulaBoxWrapper))}>
        {banner ? (
          <div className="banner-wrapper u-small-padding-right">
            <Banner message={banner} />
          </div>
        ) : (
          toolbar
        )}
        {!disableFormulas && (
          <div className="formula-box-wrapper input-group">
            <div className="input-group-btn" key="formula-group">
              <FunctionDropdown
                additionalFunctions={additionalFunctions}
                className="_functionDropdownBtn"
                isOpen={isFunctionDropdownOpen}
                primaryFunctions={functions}
                setDropdownOpenState={(isOpen) => {
                  setIsFunctionDropdownOpen(isOpen);
                }}
                onFunctionClick={onFunctionClick}
                disabled={disabledFormulaBar}
              />
            </div>
            {multiSelIndex != null && (
              <div className="current-cell-label input-group-addon" key="formula-cell-label">
                <span className="label label-primary">
                  {excess.util.colNumberToLetters(multiSelIndex[1])}
                  &nbsp;
                  {excess.util.rowIndexToNumber(multiSelIndex[0])}
                </span>
                {formattedError}
              </div>
            )}
            <FormulaEditable
              key="formula-box"
              fileId={fileId}
              tableName={tableName}
              tableNamespace={tableNamespace}
              documentName={documentName ?? null}
              isFormulaBox={true}
              className={classNames('formula-box form-control multi-editable u-cursor-text', {
                faded: disabledFormulaBar,
              })}
              onFormulaFinalize={cellEnterHandler}
              onFormulaFocus={onFormulaFocus}
              escapeHandler={cellEscapeHandler}
              index={multiSelIndex}
              cid={`${cid}-formula-box`}
              locked={disabledFormulaBar}
              rowMetadata={rowMetadata}
              getLastIndex={getLastIndex}
              disableFormulas={disableFormulas}
              rootRef={formulaEditableRef}
            />
          </div>
        )}
      </div>
    </div>
  );

  return (
    <TransitionGroup component="div" onMouseDown={mouseDownHandler}>
      <CSSTransition
        key={`table-floater-${cid}`}
        classNames="transition-fade"
        timeout={TransitionConstants.TRANSITION_FADE_DURATION_MS}
      >
        {box}
      </CSSTransition>
      {showSetPrecisionModal && (
        <CSSTransition
          key="precision-modal"
          classNames="transition-fade"
          timeout={TransitionConstants.TRANSITION_FADE_DURATION_MS}
        >
          <SetPrecisionModalClass
            open={true}
            onRequestClose={() => {
              setShowSetPrecisionModal(false);
            }}
            precisionLink={{
              value: formatting.precision,
              requestChange: (precision) => {
                onCellPropertyChange([{property: 'precision', value: precision}]);
              },
            }}
          />
        </CSSTransition>
      )}
    </TransitionGroup>
  );
}

const styles = StyleSheet.create({
  formulaBoxWrapper: {
    width: '100%',
    flexWrap: 'wrap',
    gap: '4px',
  },
});
